import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Calculator, Info, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SampleSizeCalculator = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [formula, setFormula] = useState('cochran');
  const [populationSize, setPopulationSize] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState('0.95');
  const [marginOfError, setMarginOfError] = useState('5');
  const [proportion, setProportion] = useState('50');
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  const handleCalculate = async () => {
    // Validate inputs
    if (formula === 'yamane' && !populationSize) {
      toast.error('Population size is required for Yamane\'s formula');
      return;
    }
    
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/calculate/sample-size`, {
        formula,
        population_size: populationSize ? parseInt(populationSize) : null,
        confidence_level: parseFloat(confidenceLevel),
        margin_of_error: parseFloat(marginOfError) / 100,
        population_proportion: parseFloat(proportion) / 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      toast.success('Sample size calculated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Sample Size Calculator</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Calculator className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Calculate Sample Size</h2>
                <p className="text-sm text-slate-500">For quantitative research studies</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Formula</Label>
                <Select value={formula} onValueChange={setFormula}>
                  <SelectTrigger className="mt-1" data-testid="formula-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cochran">Cochran's Formula</SelectItem>
                    <SelectItem value="yamane">Yamane's Formula</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {formula === 'cochran' 
                    ? 'Best for unknown or very large populations' 
                    : 'Simplified formula requiring known population'}
                </p>
              </div>

              <div>
                <Label>Population Size {formula === 'yamane' && '*'}</Label>
                <Input
                  type="number"
                  value={populationSize}
                  onChange={(e) => setPopulationSize(e.target.value)}
                  placeholder={formula === 'cochran' ? 'Optional (for finite correction)' : 'Required'}
                  className="mt-1"
                  data-testid="population-input"
                />
              </div>

              <div>
                <Label>Confidence Level</Label>
                <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
                  <SelectTrigger className="mt-1" data-testid="confidence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.90">90%</SelectItem>
                    <SelectItem value="0.95">95% (Recommended)</SelectItem>
                    <SelectItem value="0.99">99%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Margin of Error (%)</Label>
                <Input
                  type="number"
                  value={marginOfError}
                  onChange={(e) => setMarginOfError(e.target.value)}
                  placeholder="e.g., 5"
                  min="1"
                  max="20"
                  className="mt-1"
                  data-testid="margin-input"
                />
                <p className="text-xs text-slate-500 mt-1">Common values: 5% or 10%</p>
              </div>

              {formula === 'cochran' && (
                <div>
                  <Label>Population Proportion (%)</Label>
                  <Input
                    type="number"
                    value={proportion}
                    onChange={(e) => setProportion(e.target.value)}
                    placeholder="e.g., 50"
                    min="1"
                    max="100"
                    className="mt-1"
                    data-testid="proportion-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Use 50% if unknown (maximum sample size)</p>
                </div>
              )}

              <Button 
                onClick={handleCalculate} 
                disabled={calculating}
                className="w-full bg-indigo-600 hover:bg-indigo-700 mt-4"
                data-testid="calculate-btn"
              >
                {calculating ? 'Calculating...' : 'Calculate Sample Size'}
              </Button>
            </div>
          </div>

          {/* Results & Info */}
          <div className="space-y-6">
            {/* Result Card */}
            {result && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6" data-testid="result-card">
                <h3 className="font-semibold text-slate-900 mb-4">Calculated Sample Size</h3>
                <div className="text-center py-4">
                  <span className="text-5xl font-bold text-indigo-600">{result.sample_size}</span>
                  <p className="text-slate-600 mt-2">respondents needed</p>
                </div>
                <div className="mt-4 pt-4 border-t border-indigo-200 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Formula Used:</span>
                    <span className="font-medium text-slate-900">{result.formula}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Confidence Level:</span>
                    <span className="font-medium text-slate-900">{result.confidence_level}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Margin of Error:</span>
                    <span className="font-medium text-slate-900">{result.margin_of_error}</span>
                  </div>
                  {result.population_size && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Population Size:</span>
                      <span className="font-medium text-slate-900">{result.population_size.toLocaleString()}</span>
                    </div>
                  )}
                  {result.finite_correction_applied && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Finite Correction:</span>
                      <span className="font-medium text-green-600">Applied</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Formula Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-slate-900">About the Formulas</h3>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-medium text-slate-900">Cochran's Formula</h4>
                  <p className="text-slate-600 mt-1">
                    n₀ = (Z² × p × (1-p)) / e²
                  </p>
                  <p className="text-slate-500 mt-1">
                    Best when population is large or unknown. Includes finite population correction when N is provided.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-slate-900">Yamane's Formula</h4>
                  <p className="text-slate-600 mt-1">
                    n = N / (1 + N × e²)
                  </p>
                  <p className="text-slate-500 mt-1">
                    Simplified formula that requires knowing the population size. Commonly used in social sciences.
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <h4 className="font-medium text-slate-900">Key Terms</h4>
                  <ul className="mt-2 space-y-1 text-slate-600">
                    <li><strong>Z:</strong> Z-score based on confidence level</li>
                    <li><strong>p:</strong> Expected population proportion</li>
                    <li><strong>e:</strong> Margin of error (precision)</li>
                    <li><strong>N:</strong> Population size</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SampleSizeCalculator;
